/**
 * BulkResultsPanel - Bulk Operation Results Display
 *
 * FERP CRITICAL: Display bulk operation results with rollback capability
 *
 * Features:
 * - Summary statistics (total/success/failure/duration)
 * - Download results as CSV
 * - Error table (failed rows only)
 * - Rollback button (if within 24-hour window)
 * - Success/failure badges
 * - Complete audit trail
 *
 * Architecture Principles:
 * - Data-driven (results from BulkOperationRepository)
 * - Repository pattern
 * - Immutable audit trails
 * - Rollback support (24-hour window)
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  Stack,
  Title,
  Text,
  Table,
  Button,
  Badge,
  Group,
  Alert,
  Card,
  Modal,
  Textarea,
  Box,
} from '@mantine/core';
import {
  IconDownload,
  IconAlertCircle,
  IconCheck,
  IconX,
  IconArrowBackUp,
  IconClock,
} from '@tabler/icons-react';
import { useApolloClient } from '@apollo/client/react';
import { ApolloClient } from '@apollo/client';
import {
  BulkOperation,
  BulkOperationRepository,
  RowStatus,
  Operation,
} from '../data/repositories/BulkOperationRepository';

export interface BulkResultsPanelProps {
  /** Bulk operation to display */
  operation: BulkOperation;
  /** Callback when rollback is requested */
  onRollback?: (batchId: string, reason: string) => void;
  /** Is rollback in progress */
  isRollingBack?: boolean;
}

/**
 * Get operation color
 */
function getOperationColor(operation: Operation): string {
  switch (operation) {
    case Operation.ASSIGN:
      return 'green';
    case Operation.REVOKE:
      return 'red';
    case Operation.UPDATE_EXPIRATION:
      return 'blue';
    default:
      return 'gray';
  }
}

/**
 * Get row status color
 */
function getRowStatusColor(status: RowStatus): string {
  switch (status) {
    case RowStatus.SUCCESS:
      return 'green';
    case RowStatus.FAILED:
      return 'red';
    case RowStatus.SKIPPED:
      return 'gray';
    default:
      return 'gray';
  }
}

/**
 * Calculate duration
 */
function calculateDuration(startedAt: string, completedAt?: string): string {
  const start = new Date(startedAt).getTime();
  const end = completedAt ? new Date(completedAt).getTime() : Date.now();
  const durationMs = end - start;
  const seconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(seconds / 60);

  if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  }
  return `${seconds}s`;
}

/**
 * Bulk Results Panel
 *
 * FERP Architecture Compliance:
 * - ✅ Data-driven (results from BulkOperationRepository)
 * - ✅ Repository pattern
 * - ✅ Immutable audit trails
 * - ✅ Rollback support (24-hour window)
 */
export function BulkResultsPanel({
  operation,
  onRollback,
  isRollingBack = false,
}: BulkResultsPanelProps) {
  // State
  const [isRollbackModalOpen, setIsRollbackModalOpen] = useState(false);
  const [rollbackReason, setRollbackReason] = useState('');

  // Calculate metrics
  const duration = calculateDuration(operation.startedAt, operation.completedAt);
  const successRate = operation.totalRows > 0
    ? Math.round((operation.successCount / operation.totalRows) * 100)
    : 0;
  const failedRows = operation.results.filter(r => r.status === RowStatus.FAILED);

  /**
   * Download results as CSV
   */
  const downloadResults = useCallback(() => {
    const headers = 'Row Number,User ID,Role Name,Operation,Status,Error Message,Assignment ID\n';
    const rows = operation.results.map(row =>
      [
        row.rowNumber,
        row.userId,
        row.roleName,
        row.operation,
        row.status,
        row.errorMessage || '',
        row.assignmentId || '',
      ].join(',')
    ).join('\n');

    const csv = headers + rows;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bulk_results_${operation.batchId}_${new Date().toISOString()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [operation]);

  /**
   * Handle rollback confirm
   */
  const handleRollbackConfirm = useCallback(() => {
    if (onRollback && rollbackReason.trim()) {
      onRollback(operation.batchId, rollbackReason);
      setIsRollbackModalOpen(false);
      setRollbackReason('');
    }
  }, [onRollback, operation.batchId, rollbackReason]);

  return (
    <Stack gap="md">
      {/* Summary Statistics */}
      <Card withBorder>
        <Stack gap="md">
          <Group justify="space-between" ta="center">
            <Title order={4}>Operation Results</Title>
            <Button
              variant="light"
              leftSection={<IconDownload size={16} />}
              onClick={downloadResults}
            >
              Download Results CSV
            </Button>
          </Group>

          <Group grow>
            <Card bg="blue.0" p="md">
              <Stack gap={4}>
                <Text size="xs" c="dimmed" ta="center">
                  Total Rows
                </Text>
                <Text size="xl" fw={700} ta="center">
                  {operation.totalRows}
                </Text>
              </Stack>
            </Card>

            <Card bg="green.0" p="md">
              <Stack gap={4}>
                <Text size="xs" c="dimmed" ta="center">
                  Successful
                </Text>
                <Text size="xl" fw={700} c="green" ta="center">
                  {operation.successCount}
                </Text>
              </Stack>
            </Card>

            <Card bg="red.0" p="md">
              <Stack gap={4}>
                <Text size="xs" c="dimmed" ta="center">
                  Failed
                </Text>
                <Text size="xl" fw={700} c="red" ta="center">
                  {operation.failureCount}
                </Text>
              </Stack>
            </Card>

            <Card bg="gray.0" p="md">
              <Stack gap={4}>
                <Text size="xs" c="dimmed" ta="center">
                  Success Rate
                </Text>
                <Text size="xl" fw={700} ta="center">
                  {successRate}%
                </Text>
              </Stack>
            </Card>
          </Group>

          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              File: {operation.filename}
            </Text>
            <Text size="sm" c="dimmed">
              Duration: {duration}
            </Text>
          </Group>

          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              Processed by: {operation.processedBy}
            </Text>
            <Text size="sm" c="dimmed">
              Completed: {operation.completedAt ? new Date(operation.completedAt).toLocaleString() : 'In progress'}
            </Text>
          </Group>

          {operation.isRollbackable && onRollback && (
            <Alert
              color="yellow"
              title="Rollback Available"
              icon={<IconClock />}
            >
              <Stack gap="sm">
                <Text size="sm">
                  This operation can be rolled back within 24 hours. All successful assignments
                  will be revoked.
                </Text>
                <Button
                  color="orange"
                  leftSection={<IconArrowBackUp size={16} />}
                  onClick={() => setIsRollbackModalOpen(true)}
                  loading={isRollingBack}
                  disabled={isRollingBack}
                >
                  Rollback Operation
                </Button>
              </Stack>
            </Alert>
          )}
        </Stack>
      </Card>

      {/* Failed Rows Table */}
      {failedRows.length > 0 && (
        <Card withBorder>
          <Stack gap="md">
            <Group justify="space-between" ta="center">
              <Title order={4}>Failed Rows ({failedRows.length})</Title>
              <Badge color="red" size="lg">
                {failedRows.length} errors
              </Badge>
            </Group>

            <Box style={{ overflowX: 'auto' }}>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Row</Table.Th>
                    <Table.Th>User ID</Table.Th>
                    <Table.Th>Role Name</Table.Th>
                    <Table.Th>Operation</Table.Th>
                    <Table.Th>Error Message</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {failedRows.map((row) => (
                    <Table.Tr key={row.rowNumber}>
                      <Table.Td>{row.rowNumber}</Table.Td>
                      <Table.Td>{row.userId}</Table.Td>
                      <Table.Td>{row.roleName}</Table.Td>
                      <Table.Td>
                        <Badge color={getOperationColor(row.operation)}>
                          {row.operation}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="red">
                          {row.errorMessage || 'Unknown error'}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Box>
          </Stack>
        </Card>
      )}

      {/* Success Summary */}
      {operation.successCount > 0 && operation.failureCount === 0 && (
        <Alert color="green" title="All Rows Processed Successfully" icon={<IconCheck />}>
          Successfully processed all {operation.totalRows} rows without errors.
        </Alert>
      )}

      {/* Error Summary */}
      {operation.errorSummary && (
        <Alert color="red" title="Operation Errors" icon={<IconAlertCircle />}>
          {operation.errorSummary}
        </Alert>
      )}

      {/* Rollback Confirmation Modal */}
      <Modal
        opened={isRollbackModalOpen}
        onClose={() => setIsRollbackModalOpen(false)}
        title="Confirm Rollback"
        size="md"
      >
        <Stack gap="md">
          <Alert color="orange" icon={<IconAlertCircle />}>
            This will revoke all {operation.successCount} successful assignments from this batch.
            This action cannot be undone.
          </Alert>

          <Textarea
            label="Rollback Reason"
            description="Required - explain why you are rolling back this operation"
            placeholder="e.g., Incorrect role assignments, processing error, etc."
            value={rollbackReason}
            onChange={(e) => setRollbackReason(e.currentTarget.value)}
            minRows={3}
            required
          />

          <Group justify="flex-end">
            <Button
              variant="subtle"
              onClick={() => setIsRollbackModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              color="orange"
              onClick={handleRollbackConfirm}
              disabled={!rollbackReason.trim()}
              loading={isRollingBack}
            >
              Confirm Rollback
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

export default BulkResultsPanel;
